/**
 * Pure logic for browsing the exercise library.
 *
 * The library ships 1059 exercises across 15 categories, so the filtering and
 * paging rules here are load-bearing rather than incidental — see PAGE_WINDOW
 * below for the defect this exists to fix.
 */

export const CATEGORY_ALL = 'All';
export const EXERCISES_PER_PAGE = 24;

/** Marker for a gap in the page list. Not a page number. */
export const PAGE_GAP = 'gap';

const normalise = (value) => String(value ?? '').toLowerCase().trim();

/**
 * Filter by category and free-text search.
 *
 * Search matches name or category, so typing "squat" finds both the Squat
 * category and "Split Squat - Dumbbell" in Lunge.
 */
export function filterExercises(exercises, { search = '', category = CATEGORY_ALL } = {}) {
  const term = normalise(search);
  const wanted = category ?? CATEGORY_ALL;

  return (exercises ?? []).filter((exercise) => {
    if (wanted !== CATEGORY_ALL && exercise?.category !== wanted) {
      return false;
    }
    if (term === '') {
      return true;
    }
    return normalise(exercise?.name).includes(term) || normalise(exercise?.category).includes(term);
  });
}

/** The category list, with "All" first and the rest sorted. */
export function categoriesOf(exercises) {
  const unique = new Set(
    (exercises ?? []).map((exercise) => exercise?.category).filter((c) => c != null && c !== '')
  );
  return [CATEGORY_ALL, ...[...unique].sort()];
}

/**
 * Slice a page out of the filtered list.
 *
 * `page` is clamped, so a stale page number — say, page 40 when a filter has
 * reduced the results to two pages — yields the last page rather than an empty
 * screen with no way back.
 */
export function paginate(items, page = 1, perPage = EXERCISES_PER_PAGE) {
  const all = items ?? [];
  const totalPages = Math.max(1, Math.ceil(all.length / perPage));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), totalPages);
  const start = (current - 1) * perPage;

  return {
    items: all.slice(start, start + perPage),
    page: current,
    totalPages,
    total: all.length,
    firstIndex: all.length === 0 ? 0 : start + 1,
    lastIndex: Math.min(start + perPage, all.length),
  };
}

/**
 * The page numbers to render, with gaps.
 *
 * The previous implementation was `Array.from({length: totalPages}, ...)` — one
 * button per page. With 1059 exercises at 10 per page that is **106 buttons**
 * rendered at once, wrapping across the viewport and unusable on a phone.
 *
 * This returns first page, last page, and a window around the current one, with
 * PAGE_GAP markers between non-adjacent runs:
 *
 *     1 … 6 7 [8] 9 10 … 106
 *
 * @param {number} current
 * @param {number} totalPages
 * @param {number} radius pages to show either side of current
 * @returns {Array<number|'gap'>}
 */
export function pageWindow(current, totalPages, radius = 1) {
  if (!Number.isFinite(totalPages) || totalPages < 1) return [];
  if (totalPages === 1) return [1];

  const page = Math.min(Math.max(1, Math.floor(current) || 1), totalPages);

  const wanted = new Set([1, totalPages]);
  for (let offset = -radius; offset <= radius; offset += 1) {
    const candidate = page + offset;
    if (candidate >= 1 && candidate <= totalPages) {
      wanted.add(candidate);
    }
  }

  const sorted = [...wanted].sort((a, b) => a - b);
  const result = [];

  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1] > 1) {
      result.push(PAGE_GAP);
    }
    result.push(value);
  });

  return result;
}
